import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Boxes,
  Globe2,
  Languages,
  LayoutDashboard,
  MessageCircleMore,
  PackageCheck,
  SearchCheck,
  ShoppingCart,
  Sparkles,
  Store,
  WalletCards,
} from "lucide-react";

export type PaidPlan = "PRO" | "EXPORT";
export type PlanCode = "FREE" | PaidPlan;
export type PlanSlug = "siap-digital" | "siap-profesional" | "siap-export";

export type PlanDefinition = {
  code: PlanCode;
  slug: PlanSlug;
  name: string;
  shortName: string;
  eyebrow: string;
  price: number;
  priceLabel: string;
  suffix: string;
  description: string;
  hero: string;
  supportingText: string;
  primaryCta: string;
  audience: string[];
  features: Array<{ icon: LucideIcon; title: string; text: string }>;
  limitations: string[];
  steps: string[];
};

export const plans: Record<PlanSlug, PlanDefinition> = {
  "siap-digital": {
    code: "FREE",
    slug: "siap-digital",
    name: "Siap Digital",
    shortName: "Digital",
    eyebrow: "Mulai gratis lewat WhatsApp",
    price: 0,
    priceLabel: "Rp0",
    suffix: "selamanya",
    description: "Website gratis untuk UMKM yang ingin langsung punya etalase jualan sendiri, cukup melalui WhatsApp.",
    hero: "Mulai dari satu chat. Punya website jualan tanpa ribet.",
    supportingText: "Kirim nama usaha, produk, harga, foto, dan QRIS. Setelah datanya lengkap, CSJW menyiapkan website yang siap dibagikan kepada pembeli.",
    primaryCta: "Buat website gratis",
    audience: ["Baru mulai berjualan online", "Hanya memiliki satu produk utama", "Ingin proses paling sederhana", "Belum membutuhkan dashboard"],
    features: [
      { icon: MessageCircleMore, title: "WhatsApp-only", text: "Pembuatan dan edit sederhana dilakukan langsung lewat chat." },
      { icon: Store, title: "Satu website aktif", text: "URL publik yang siap dibagikan ke pelanggan." },
      { icon: Sparkles, title: "Tema premium", text: "AI memilih komposisi dari theme catalog yang terkontrol." },
      { icon: WalletCards, title: "QRIS statis UMKM", text: "Pembayaran masuk langsung ke QRIS penjual setelah total akhir dikonfirmasi melalui WhatsApp." },
      { icon: PackageCheck, title: "Pesan tanpa backend", text: "Jumlah, subtotal, dan lokasi dikirim langsung ke WhatsApp; transaksi tidak disimpan CSJW." },
      { icon: SearchCheck, title: "Mudah diperbarui", text: "Gunakan #HARGA, #FOTO, #QRIS, dan perintah singkat lain." },
    ],
    limitations: ["Tanpa dashboard", "Maksimal satu produk", "Ongkir dan total akhir dihitung UMKM", "QRIS statis dan konfirmasi pembayaran manual", "Transaksi tidak tercatat di backend CSJW"],
    steps: ["Klik tombol WhatsApp", "Kirim brief #CSJW, foto produk, dan QRIS", "Website langsung dibuat", "Pembeli mengirim jumlah dan lokasi ke WhatsApp UMKM", "UMKM memberi ongkir dan total akhir, lalu pembeli membayar via QRIS statis"],
  },
  "siap-profesional": {
    code: "PRO",
    slug: "siap-profesional",
    name: "Siap Profesional",
    shortName: "Profesional",
    eyebrow: "Untuk bisnis yang mulai bertumbuh",
    price: 49000,
    priceLabel: "Rp49.000",
    suffix: "/ bulan • harga pilot",
    description: "Paket lengkap untuk usaha yang membutuhkan banyak produk, tampilan fleksibel, pesanan, dan laporan dalam satu dashboard.",
    hero: "Tampil lebih profesional. Kelola jualan dari satu dashboard.",
    supportingText: "Setelah paket aktif, WhatsApp menjadi cara cepat untuk memulai dan dashboard menjadi pusat kendali website, produk, pesanan, serta performa usaha.",
    primaryCta: "Berlangganan Siap Profesional",
    audience: ["Memiliki banyak produk", "Membutuhkan cart dan order", "Ingin mengatur tampilan sendiri", "Membutuhkan analitik dan custom domain"],
    features: [
      { icon: LayoutDashboard, title: "Dashboard lengkap", text: "Kelola website, produk, order, pembayaran, dan pengaturan." },
      { icon: Boxes, title: "Produk lebih banyak", text: "Katalog dapat berkembang mengikuti kebutuhan usaha." },
      { icon: ShoppingCart, title: "Cart & checkout", text: "Pembeli dapat memilih beberapa produk dalam satu transaksi." },
      { icon: Sparkles, title: "Section Builder", text: "Atur section, variant, konten, urutan, preview, dan publish." },
      { icon: BarChart3, title: "Analitik bisnis", text: "Pantau kunjungan, klik WhatsApp, order, dan performa dasar." },
      { icon: Globe2, title: "Custom domain", text: "Gunakan alamat bisnis sendiri ketika sudah siap." },
    ],
    limitations: ["Pembayaran paket harus berhasil sebelum trigger aktif", "Fitur buyer dan export belum termasuk", "Fair-use untuk produk dan AI"],
    steps: ["Daftarkan nomor WhatsApp", "Selesaikan pembayaran", "Lanjut via WhatsApp dengan #CSJW", "Aktifkan dashboard dan kelola website"],
  },
  "siap-export": {
    code: "EXPORT",
    slug: "siap-export",
    name: "Siap Export",
    shortName: "Export",
    eyebrow: "Dari produk lokal menuju peluang global",
    price: 149000,
    priceLabel: "Rp149.000",
    suffix: "/ bulan • harga pilot",
    description: "Paket paling lengkap untuk UMKM yang ingin membangun profil global, menilai kesiapan, dan mengelola peluang buyer.",
    hero: "Buat produk lokalmu lebih siap dilihat dan dipahami buyer global.",
    supportingText: "Mulai dari website dua bahasa, lalu lengkapi kapasitas, MOQ, kemasan, dokumen, pasar prioritas, dan komunikasi buyer melalui dashboard Export.",
    primaryCta: "Berlangganan Siap Export",
    audience: ["Sudah memiliki kapasitas produksi", "Ingin membuat profil bilingual", "Membutuhkan MOQ dan spesifikasi", "Ingin mengelola inquiry dan buyer opportunity"],
    features: [
      { icon: LayoutDashboard, title: "Semua fitur Profesional", text: "Dashboard, builder, katalog, cart, order, dan analitik." },
      { icon: Languages, title: "Profil bilingual", text: "Konten Indonesia dan Inggris untuk buyer internasional." },
      { icon: SearchCheck, title: "Export readiness", text: "Checklist dan skor yang menjelaskan bagian yang perlu dilengkapi." },
      { icon: PackageCheck, title: "Spesifikasi ekspor", text: "MOQ, kapasitas, lead time, kemasan, shelf life, dan sertifikasi." },
      { icon: Globe2, title: "Buyer opportunity", text: "Kelola peluang buyer, match score, inquiry, dan tindak lanjut." },
      { icon: WalletCards, title: "Quotation workflow", text: "Dukung permintaan harga, sampel, dan komunikasi B2B." },
    ],
    limitations: ["Pembayaran paket harus berhasil sebelum trigger aktif", "Data readiness perlu dilengkapi pengguna", "Peluang buyer tidak menjamin transaksi ekspor"],
    steps: ["Daftarkan nomor WhatsApp", "Selesaikan pembayaran", "Buat website dasar via #CSJW", "Aktifkan dashboard Export", "Lengkapi profil dan kesiapan ekspor"],
  },
};

export function getPlanBySlug(slug?: string) {
  return slug && slug in plans ? plans[slug as PlanSlug] : null;
}

export function formatRupiah(value: number) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value);
}
